import { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import { useAlert } from 'react-alert';
import './styles/lecturesZone.scss'
import PageHeader from '../../components/PageHeader';
import ConfirmModal from '../../components/ConfirmModal';
import IndividualJourney from './IndividualJourney';
import SlotPicker from './SlotPicker';
import { useJourney } from '../../hooks/useJourney';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { readJson } from '../../apiHelper/readJson';
import { MONTHS, time, groupByDay } from './lessonDates';

type Lesson = {
    id: string;
    start: string;
    durationMinutes: number;
    topic: string;
    meetUrl: string;
    recordingUrl: string | null;
    isGroup: boolean;
    isPast: boolean;
    canCancel: boolean;
};

type Slot = { id: string; start: string; durationMinutes: number };

export default function LecturesZone() {
    const [upcoming, setUpcoming] = useState<Lesson[]>([]);
    const [past, setPast] = useState<Lesson[]>([]);
    const [credit, setCredit] = useState(0);
    const [slots, setSlots] = useState<Slot[]>([]);
    const [loading, setLoading] = useState(true);
    const [cancelId, setCancelId] = useState<string | null>(null);

    const alert = useAlert();
    const api = new ApiHelper();
    const { journey, loading: journeyLoading, reload: reloadJourney } = useJourney();

    async function fetchAll() {
        try {
            const [schedRes, bookRes] = await Promise.all([
                api.onlineLessonsApi.getScheduleList(),
                api.onlineLessonsApi.getBookingList(),
            ]);
            const sched = await readJson(schedRes);
            setUpcoming(sched.upcoming ?? []);
            setPast(sched.past ?? []);
            const book = await readJson(bookRes);
            setCredit(book.creditBalance ?? 0);
            setSlots(book.slots ?? []);
        } catch (x: any) {
            api.processErrors(x);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { fetchAll(); }, []);

    async function book(slotId: string) {
        try {
            await api.onlineLessonsApi.bookCreate(slotId);
            alert.success("Termín byl rezervován");
            fetchAll();
        } catch (x: any) { api.processErrors(x); }
    }

    async function cancel() {
        if (!cancelId) return;
        try {
            await api.onlineLessonsApi.cancelCreate(cancelId);
            alert.success("Rezervace zrušena, kredit byl vrácen");
            fetchAll();
        } catch (x: any) {
            api.processErrors(x);
        } finally {
            setCancelId(null);
        }
    }

    // Wait for the journey before deciding what to show — during term voting or
    // the waiting-to-start phase there is no schedule yet, so an empty calendar
    // would be misleading.
    if (journeyLoading) {
        return (
            <div className="lecturesZone">
                <Link to=".." className="backLink">← Zpět na rozcestník</Link>
                <PageHeader title="Online lekce" subtitle="Rozvrh, rezervace a záznamy lekcí s lektorem" />
                <div className="card"><p className="muted">Načítání…</p></div>
            </div>
        );
    }

    // Welcome (not yet in a group) / term voting / waiting-to-start: show the
    // status-driven journey (welcome, vote on a term, or "starts soon" with the
    // first-lecture date) instead of the rozvrh. It already includes the extra-
    // lesson booking calendar. Running / done — and booking-only students with no
    // journey — fall through to the schedule, booking and recordings view below.
    if (journey && (journey.status === -1 || journey.status === 0 || journey.status === 1)) {
        return (
            <div className="lecturesZone">
                <Link to=".." className="backLink">← Zpět na rozcestník</Link>
                <IndividualJourney journey={journey} onChanged={reloadJourney} />
            </div>
        );
    }

    const recordings = past.filter(l => l.recordingUrl);
    const empty = !loading && upcoming.length === 0 && past.length === 0;
    const upcomingDays = groupByDay(upcoming);

    return (
        <div className="lecturesZone">
            <Link to=".." className="backLink">← Zpět na rozcestník</Link>
            <PageHeader title="Online lekce" subtitle="Rozvrh, rezervace a záznamy lekcí s lektorem" />

            {!loading && !empty &&
                <div className="statRow">
                    <div className="stat"><span className="num">{upcoming.length}</span><span className="lbl">nadcházejících</span></div>
                    <div className="stat"><span className="num">{recordings.length}</span><span className="lbl">záznamů</span></div>
                    <div className="stat credit"><span className="num">{credit}</span><span className="lbl">kreditů</span></div>
                </div>
            }

            {loading ?
                <div className="card"><p className="muted">Načítání…</p></div>
                : empty ?
                <div className="card placeholder">
                    <h3>Zatím tu nic není</h3>
                    <p className="muted">Jakmile bude naplánovaná lekce nebo si rezervuješ termín, objeví se ti tady rozvrh s tématy, odkazy na online schůzku a záznamy.</p>
                </div>
                :
                <>
                    {upcoming.length > 0 &&
                        <section className="card">
                            <h2 className="cardTitle">Nadcházející lekce</h2>
                            <div className="timeline">
                                {upcomingDays.map((g, gi) => (
                                    <div key={gi} className="tlDay">
                                        <div className="dateBox">
                                            <span className="d">{g.day}.</span>
                                            <span className="m">{g.month}</span>
                                            <span className="wd">{g.weekday}</span>
                                        </div>
                                        <div className="tlItems">
                                            {g.items.map(l => (
                                                <div key={l.id} className="agendaCard">
                                                    <div className="meta">
                                                        <span className="time">{time(l.start)} · {l.durationMinutes} min</span>
                                                        <h3>{l.topic}</h3>
                                                        <span className={"tag " + (l.isGroup ? "group" : "solo")}>{l.isGroup ? "skupinová" : "individuální"}</span>
                                                    </div>
                                                    <div className="acts">
                                                        {l.meetUrl
                                                            ? <div className="btn join" onClick={() => window.open(l.meetUrl, "_blank", "noopener")}>Připojit se</div>
                                                            : <span className="muted">Odkaz bude doplněn</span>}
                                                        {l.canCancel && <div className="btn cancel" onClick={() => setCancelId(l.id)}>Zrušit</div>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    }

                    <section className="card booking">
                        <h2 className="cardTitle">Rezervovat individuální lekci</h2>
                        <p className="credit">Máš <b>{credit}</b> {credit === 1 ? "kredit" : (credit >= 2 && credit <= 4 ? "kredity" : "kreditů")} na individuální lekce.</p>

                        {credit <= 0 ?
                            <p className="muted">Nemáš žádný kredit. <Link to="/kurz/ucet">Dokup v Účtu →</Link></p>
                            : slots.length === 0 ?
                            <p className="muted">Momentálně nejsou volné žádné termíny.</p>
                            :
                            <SlotPicker slots={slots} onBook={book} />
                        }
                        <p className="hint">Rezervaci můžeš zdarma zrušit nejpozději 2 dny před začátkem — kredit se ti vrátí.</p>
                    </section>

                    {recordings.length > 0 &&
                        <section className="card">
                            <h2 className="cardTitle">Záznamy lekcí</h2>
                            <div className="recordings">
                                {recordings.map(l => (
                                    <div key={l.id} className="recCard" onClick={() => window.open(l.recordingUrl!, "_blank")}>
                                        <div className="play"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7L8 5Z" /></svg></div>
                                        <div className="recInfo">
                                            <h3>{l.topic}</h3>
                                            <span>{new Date(l.start).getDate()}. {MONTHS[new Date(l.start).getMonth()]}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    }
                </>
            }

            <ConfirmModal
                open={cancelId !== null}
                title="Zrušit rezervaci?"
                message="Rezervace bude zrušena a kredit ti bude vrácen na účet."
                confirmLabel="Zrušit rezervaci"
                cancelLabel="Zpět"
                onConfirm={cancel}
                onClose={() => setCancelId(null)}
            />
        </div>
    );
}
